import React, { useState, useEffect, useRef } from 'react';
import CookieBlockedMessage from '../../CommonComponents/CookieBlockedMessage';
import SafeIFrame from '../../CommonComponents/SafeIFrame';
import { shouldShowEmbeddedContent } from '../../../utis/cookieConsentUtils';

// Component to handle script-based embeds (ThriveCart, Calendly, etc.)
// Uses iframe isolation so each embed has its own JavaScript context
const ScriptEmbed = ({ htmlContent, height, width }) => {
    const iframeRef = useRef(null);

    useEffect(() => {
        if (!iframeRef.current || !htmlContent) return;

        const iframe = iframeRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        // Create complete HTML document for iframe
        const fullHTML = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body {
            margin: 0;
            padding: 0;
            overflow: auto;
        }
    </style>
</head>
<body>
    ${htmlContent}
</body>
</html>
        `;

        // Write HTML to iframe
        iframeDoc.open();
        iframeDoc.write(fullHTML);
        iframeDoc.close();

    }, [htmlContent]);

    return (
        <iframe
            ref={iframeRef}
            style={{
                width: `${width}px`,
                height: `${height}px`,
                border: 'none',
                overflow: 'auto'
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            title="Embed Content"
        />
    );
};

const Element = ({ elements, dimensions }) => {
    // State to force re-render when cookie consent changes
    const [, setCookieStatusChanged] = useState(0);

    // Listen for cookie consent changes to trigger re-render
    useEffect(() => {
        const handleCookieConsentChange = () => {
            setCookieStatusChanged(prev => prev + 1);
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('cookieConsentChanged', handleCookieConsentChange);
            return () => {
                window.removeEventListener('cookieConsentChanged', handleCookieConsentChange);
            };
        }
    }, []);
    const renderElement = (element, itemKey) => {
        if (element?.element_type_name?.toLowerCase() == 'image') {
            return (
                <div>
                <img
                    key={itemKey}
                    src={element?.file_url ?? element?.value?.gif_link}
                    alt=""
                    style={{
                        height: `${element?.file_details?.dimensions?.height ?? dimensions?.height}px`,
                        width: `${element?.file_details?.dimensions?.width ?? dimensions?.width}px`,
                        maxWidth: '100%'
                    }}
                />
                </div>
            );
        } else if (element?.element_type_name?.toLowerCase() == 'video' || element?.element_type_name?.toLowerCase() === "web url") {
            // Check if embedded content should be shown based on cookie consent
            // Cookie blocking only applies in viewer mode (LaserLink flow where cookie modal is shown)
            const canShowEmbedded = shouldShowEmbeddedContent();

            if (!canShowEmbedded) {
                return (
                    <CookieBlockedMessage
                        key={itemKey}
                        style={{
                            height: `${element?.geometry?.height ?? dimensions?.height}px`,
                            width: `${element?.geometry?.width ?? dimensions?.width}px`,
                        }}
                        dimensions={dimensions}
                    />
                );
            }

            // SIMPLE FIX: If video_link contains <script> tag, inject HTML directly (ThriveCart, etc.)
            if (/<script/i.test(element?.value?.video_link)) {
                return (
                    <ScriptEmbed
                        key={itemKey}
                        htmlContent={element.value.video_link}
                        height={element?.geometry?.height ?? dimensions?.height}
                        width={element?.geometry?.width ?? dimensions?.width}
                    />
                );
            }

            // Standard iframe embed (YouTube, Vimeo, etc.)
            return (
                <SafeIFrame
                    key={itemKey}
                    style={{
                        height: `${element?.geometry?.height ?? dimensions?.height}px`,
                        width: `${element?.geometry?.width ?? dimensions?.width}px`,
                    }}
                    scrolling="yes"
                    src={element?.value?.video_link}
                    title="url"
                />
            );
        } else if (element?.element_type_name?.toLowerCase() == 'html text') {
            return <div className='bg-white' style={{ width: `${dimensions?.width}px`, height: `${dimensions?.height}px` }} dangerouslySetInnerHTML={{ __html: element.value.html_text }} />
        }
        return "Not an Element";
    }
    return (
        <>
            {
                Object.keys(elements)?.map((elementKey, itemKey) => <div key={itemKey}>{renderElement(elements[elementKey], itemKey)}</div>)
            }
        </>
    )
}
export default Element;